'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/local-api/client';
import { XMarkIcon, MagnifyingGlassIcon, UserPlusIcon, CheckIcon, UserMinusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import Link from 'next/link';

interface UserProfile {
  id: string;
  full_name: string;
  username?: string;
  bio?: string;
  role?: string;
  followers_count: number;
  following_count: number;
  posts_count: number;
  avatar_url?: string;
}

interface PeopleSearchProps {
  currentUserId?: string;
  onClose: () => void;
  initialSentRequests?: Set<string>;
  initialConnections?: Set<string>;
  initialFollowing?: Set<string>;
  initialReceivedRequests?: Set<string>;
  onSendRequest?: (receiverId: string) => void;
  onFollow?: (userId: string) => void;
  onUnfollow?: (userId: string) => void;
}

export default function PeopleSearch({
  currentUserId,
  onClose,
  initialSentRequests = new Set(),
  initialConnections = new Set(),
  initialFollowing = new Set(),
  initialReceivedRequests = new Set(),
  onSendRequest,
  onFollow,
  onUnfollow,
}: PeopleSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserProfile[]>([]);
  const [suggested, setSuggested] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set(initialSentRequests));
  const [connections, setConnections] = useState<Set<string>>(new Set(initialConnections));
  const [following, setFollowing] = useState<Set<string>>(new Set(initialFollowing));
  const [receivedRequests, setReceivedRequests] = useState<Set<string>>(new Set(initialReceivedRequests));
  const [processing, setProcessing] = useState<string | null>(null);
  const [relationshipsLoading, setRelationshipsLoading] = useState(Boolean(currentUserId));
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    const client = createClient();
    setRelationshipsLoading(true);
    async function loadRelationships() {
      try {
        const [requests, friends] = await Promise.all([
          client.from('connection_requests').select('sender_id, receiver_id, status')
            .or(`sender_id.eq.${currentUserId},receiver_id.eq.${currentUserId}`)
            .in('status', ['pending', 'accepted']),
          client.from('connections').select('user_id_1, user_id_2')
            .or(`user_id_1.eq.${currentUserId},user_id_2.eq.${currentUserId}`),
        ]);
        if (cancelled) return;
        if (requests.error || friends.error) throw new Error('Could not load friends');
        const sent = new Set<string>();
        const received = new Set<string>();
        const connected = new Set<string>();
        for (const row of friends.data ?? []) {
          connected.add(row.user_id_1 === currentUserId ? row.user_id_2 : row.user_id_1);
        }
        for (const row of requests.data ?? []) {
          if (row.status === 'accepted') connected.add(row.sender_id === currentUserId ? row.receiver_id : row.sender_id);
          else if (row.sender_id === currentUserId) sent.add(row.receiver_id);
          else received.add(row.sender_id);
        }
        setConnections(connected);
        setSentRequests(sent);
        setReceivedRequests(received);
        setRelationshipsLoading(false);
      } catch {
        if (!cancelled) toast.error('Could not load friend status. Please reopen people search to retry.');
      }
    }
    void loadRelationships();
    return () => { cancelled = true; };
  }, [currentUserId]);

  const fetchSuggested = useCallback(async () => {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
      .neq('id', currentUserId ?? '')
      .order('followers_count', { ascending: false })
      .limit(12);
    if (data) setSuggested(data as UserProfile[]);
  }, [currentUserId]);

  const fetchFollowing = useCallback(async () => {
    if (!currentUserId) return;
    const supabase = createClient();
    const { data } = await supabase.from('follows').select('following_id').eq('follower_id', currentUserId);
    if (data) setFollowing(new Set(data.map((f: any) => f.following_id)));
  }, [currentUserId]);

  useEffect(() => {
    fetchSuggested();
    fetchFollowing();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [fetchSuggested, fetchFollowing]);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
      .neq('id', currentUserId ?? '')
      .or(`full_name.ilike.%${q}%,username.ilike.%${q}%,bio.ilike.%${q}%`)
      .limit(20);
    setResults((data as UserProfile[]) ?? []);
    setLoading(false);
  }, [currentUserId]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 350);
  };

  const handleSendRequest = async (userId: string) => {
    if (!currentUserId) { toast.error('Sign in to connect'); return; }
    setProcessing(userId);
    const supabase = createClient();
    const { error } = await supabase.from('connection_requests').insert({
      sender_id: currentUserId,
      receiver_id: userId,
      status: 'pending',
    });
    if (error) {
      toast.error('Could not send request');
    } else {
      setSentRequests((prev) => new Set([...prev, userId]));
      // Auto-follow when sending request
      await supabase.from('follows').upsert({ follower_id: currentUserId, following_id: userId });
      setFollowing((prev) => new Set([...prev, userId]));
      toast.success('Friend request sent!');
      onSendRequest?.(userId);
    }
    setProcessing(null);
  };

  const handleFollow = async (userId: string) => {
    if (!currentUserId) { toast.error('Sign in to follow'); return; }
    setProcessing(userId);
    const supabase = createClient();
    const { error } = await supabase.from('follows').upsert({ follower_id: currentUserId, following_id: userId });
    if (!error) {
      setFollowing((prev) => new Set([...prev, userId]));
      toast.success('Following!');
      onFollow?.(userId);
    } else {
      toast.error('Could not follow');
    }
    setProcessing(null);
  };

  const handleUnfollow = async (userId: string) => {
    if (!currentUserId) return;
    setProcessing(userId);
    const supabase = createClient();
    await supabase.from('follows').delete().eq('follower_id', currentUserId).eq('following_id', userId);
    setFollowing((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    toast.success('Unfollowed');
    onUnfollow?.(userId);
    setProcessing(null);
  };

  const displayList = query.trim() ? results : suggested;

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  const gradients = [
    'from-blue-500 to-purple-600',
    'from-emerald-500 to-teal-600',
    'from-orange-500 to-red-500',
    'from-pink-500 to-rose-600',
    'from-violet-500 to-indigo-600',
    'from-amber-500 to-orange-600',
  ];

  const getGradient = (id: string) => gradients[id.charCodeAt(0) % gradients.length];

  const UserCard = ({ user }: { user: UserProfile }) => {
    const isConnected = connections.has(user.id);
    const isRequested = sentRequests.has(user.id);
    const isReceivedPending = receivedRequests.has(user.id);
    const isFollowing = following.has(user.id);
    const isProcessing = processing === user.id || relationshipsLoading;

    return (
      <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted/40 transition-colors group">
        {/* Avatar */}
        <Link href={`/community-network/profile/${user.id}`} onClick={onClose} className="flex-shrink-0">
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getGradient(user.id)} flex items-center justify-center text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity`}>
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name} className="w-full h-full rounded-full object-cover" />
            ) : (
              getInitials(user.full_name || 'U')
            )}
          </div>
        </Link>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <Link href={`/community-network/profile/${user.id}`} onClick={onClose}>
            <p className="text-sm font-bold text-foreground truncate hover:text-[#1877f2] transition-colors cursor-pointer">
              {user.full_name}
            </p>
          </Link>
          {user.username && (
            <p className="text-[11px] text-muted-foreground">@{user.username}</p>
          )}
          <p className="text-[11px] text-muted-foreground capitalize mt-0.5">
            {user.role === 'admin' ? '⚡ Admin' : user.role === 'moderator' ? '🛡️ Moderator' : '👤 Member'}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[10px] text-muted-foreground">
              <span className="font-semibold text-foreground">{user.followers_count ?? 0}</span> followers
            </span>
            <span className="text-[10px] text-muted-foreground">
              <span className="font-semibold text-foreground">{user.posts_count ?? 0}</span> posts
            </span>
          </div>
          {user.bio && (
            <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{user.bio}</p>
          )}
        </div>

        {/* Actions */}
        {currentUserId && (
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            {isConnected ? (
              <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-600 text-[11px] font-semibold">
                <CheckIcon style={{ width: 11, height: 11 }} />
                Friends
              </span>
            ) : isRequested ? (
              <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold">
                <CheckIcon style={{ width: 11, height: 11 }} />
                Requested
              </span>
            ) : isReceivedPending ? (
              <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-600 text-[11px] font-semibold">
                <UserPlusIcon style={{ width: 11, height: 11 }} />
                Pending
              </span>
            ) : (
              <button
                onClick={() => handleSendRequest(user.id)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1877f2] text-white text-[11px] font-semibold hover:bg-[#166fe5] transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <UserPlusIcon style={{ width: 11, height: 11 }} />
                )}
                Add Friend
              </button>
            )}

            {isFollowing ? (
              <button
                onClick={() => handleUnfollow(user.id)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
              >
                <UserMinusIcon style={{ width: 11, height: 11 }} />
                Unfollow
              </button>
            ) : (
              !isConnected && !isRequested && !isReceivedPending && (
                <button
                  onClick={() => handleFollow(user.id)}
                  disabled={isProcessing}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-muted/80 transition-colors disabled:opacity-50"
                >
                  Follow
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#1877f2]/20 flex items-center justify-center">
            <UsersIcon className="text-[#1877f2]" style={{ width: 16, height: 16 }} />
          </div>
          <h2 className="text-base font-bold text-foreground flex-1">Find People</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground transition-colors"
          >
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* Search bar */}
        {!currentUserId && (
          <p className="px-4 pt-3 text-xs text-muted-foreground">
            <Link href="/login" onClick={onClose} className="font-semibold text-[#1877f2] hover:underline">Sign in</Link> to add friends and follow people.
          </p>
        )}
        <div className="px-4 py-3 border-b border-border flex-shrink-0">
          <div className="relative">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              style={{ width: 16, height: 16 }}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleQueryChange}
              placeholder="Search by name, username, or bio..."
              className="w-full bg-muted border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#1877f2]/50 transition-all"
            />
            {loading && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#1877f2]/30 border-t-[#1877f2] rounded-full animate-spin" />
            )}
          </div>
        </div>

        {/* Section label */}
        <div className="px-4 pt-3 pb-1 flex-shrink-0">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
            {query.trim() ? `Results for "${query}"` : 'Suggested People'}
          </p>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1 px-2 pb-3">
          {displayList.length === 0 && !loading ? (
            <div className="text-center py-10">
              <UsersIcon className="text-muted-foreground/40 mx-auto mb-2" style={{ width: 32, height: 32 }} />
              <p className="text-sm text-muted-foreground">
                {query.trim() ? 'No users found' : 'No users to suggest yet'}
              </p>
            </div>
          ) : (
            displayList.map((user) => <UserCard key={user.id} user={user} />)
          )}
        </div>
      </div>
    </div>
  );
}
